"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import { IconArrowRight, IconArrowUpRight } from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import {
  HARDWARE_CHIPS,
  MODEL_FAMILIES,
  RELEASE_MODELS,
  SUPPORT_MATRIX,
  familySlugForName,
  formatMatrixDate,
  slugify,
  type SupportRow,
} from "./photon-data";
import { PhotonPageMark } from "./photon-wordmark";

const RESULTS_URL = "/photon/performance";
const CONTACT_URL = "mailto:contact@moondream.ai";

type Filters = {
  model: string;
  hardware: string;
  capability: string;
  precision: string;
};

const DEFAULT_FILTERS: Filters = {
  model: "any",
  hardware: "any",
  capability: "any",
  precision: "any",
};

type ViewMode = "model" | "hardware";
type SortKey = "model" | "target" | "tested" | "since" | "verified";
type SortDir = "asc" | "desc";

const uniqueSorted = (values: string[]) =>
  [...new Set(values)].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

const MODEL_OPTIONS = uniqueSorted(SUPPORT_MATRIX.map((r) => r.model));
const HARDWARE_OPTIONS = uniqueSorted(SUPPORT_MATRIX.map((r) => r.chip));
const CAPABILITY_OPTIONS = [
  ...new Set(SUPPORT_MATRIX.flatMap((r) => r.capabilities)),
];
const PRECISION_OPTIONS = uniqueSorted(
  SUPPORT_MATRIX.flatMap((r) => r.precision),
);

const sortValue = (row: SupportRow, key: SortKey): string | number | null => {
  switch (key) {
    case "model":
      return row.model;
    case "target":
      return row.target;
    case "tested":
      return row.testedHardware;
    case "since":
      return row.since ? parseFloat(row.since) : null;
    case "verified":
      return row.lastVerified;
  }
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  format,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  format?: (value: string) => string;
}) => (
  <label className="filter-field">
    <span className="filter-label mono">{label}</span>
    <select
      className="filter-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {format ? format(option) : option}
        </option>
      ))}
    </select>
  </label>
);

const BenchmarkCell = ({ row }: { row: SupportRow }) => {
  if (!row.benchmarked) {
    return <span className="matrix-dim">–</span>;
  }
  return (
    <Link href={RESULTS_URL} className="family-cta ev-link">
      Results <IconArrowUpRight size={11} />
    </Link>
  );
};

const ModelCell = ({ row }: { row: SupportRow }) => (
  <>
    <Link
      href={`/photon/models/${familySlugForName(row.family)}`}
      className="matrix-model"
    >
      {row.model} <IconArrowRight size={11} />
    </Link>
    <span className="matrix-family mono">{row.family}</span>
  </>
);

// Column ordering for the coverage grid: the chips Photon has shipped on,
// per the supported-hardware list on docs.moondream.ai and the kestrel
// README kernel targets. Only chips with production support on the current
// release become columns; this list just fixes their order.
const PHOTON_1X_CHIPS = [
  "B200",
  "H200",
  "H100",
  "GH200",
  "A100",
  "A40",
  "L40S",
  "L4",
  "A10",
  "RTX PRO 6000",
  "RTX 4090",
  "RTX 30 series",
  "Jetson AGX Thor",
  "Jetson AGX Orin",
  "Jetson Orin NX",
  "Jetson Orin Nano",
];

const CoverageGrid = () => {
  const productionRows = SUPPORT_MATRIX.filter(
    (r) => r.status === "production",
  );
  const productionChips = new Set(productionRows.map((r) => r.chip));
  const chips = [
    // No empty columns: a chip earns its place by running at least one
    // production model on the current release.
    ...PHOTON_1X_CHIPS.filter((chip) => productionChips.has(chip)),
    // Future chips land automatically: append any matrix chip the ordering
    // list doesn't know about yet.
    ...[...productionChips].filter((chip) => !PHOTON_1X_CHIPS.includes(chip)),
  ];
  // One row per distinct support profile: releases that cover exactly the
  // same chip set collapse into a single row.
  // Rows split only when support actually differs - Moondream 2 runs on the
  // small Jetsons where Moondream 3 does not.
  const modelById = new Map(RELEASE_MODELS.map((m) => [m.id, m]));
  const chipsPerModel = new Map<string, Set<string>>();
  for (const row of productionRows) {
    const set = chipsPerModel.get(row.modelSlug) ?? new Set<string>();
    set.add(row.chip);
    chipsPerModel.set(row.modelSlug, set);
  }
  const groups = new Map<string, { modelIds: string[]; chips: Set<string> }>();
  for (const [modelId, modelChips] of chipsPerModel) {
    const model = modelById.get(modelId);
    if (!model) throw new Error(`Unknown model in support matrix: ${modelId}`);
    const signature = [...modelChips].sort().join("|");
    const key = `${model.family}|${model.variant ?? ""}|${signature}`;
    const group = groups.get(key) ?? { modelIds: [], chips: modelChips };
    group.modelIds.push(modelId);
    groups.set(key, group);
  }
  const familyOrder = MODEL_FAMILIES.map((f) => f.name);
  const numeric = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true });
  const rows = [...groups.entries()]
    .map(([key, group]) => {
      const groupModels = group.modelIds.map((id) => {
        const model = modelById.get(id);
        if (!model) throw new Error(`Unknown model: ${id}`);
        return model;
      });
      const { family, variant } = groupModels[0];
      const releases = [
        ...new Set(
          groupModels.map((m) => m.release).filter((r): r is string => !!r),
        ),
      ].sort(numeric);
      const label = variant
        ? `${family} ${variant}`
        : `${family} ${releases.join(" · ")}`;
      const sortToken = variant ?? releases.join(" · ");
      return {
        key,
        label,
        // Minor releases this row covers, rendered as a small subtle line
        // under the label - skipped when the label already is the release
        // (e.g. "Moondream 3").
        releases,
        showReleases: !!variant && releases.length > 0,
        chips: group.chips,
        family,
        familySlug: familySlugForName(family),
        sortToken,
      };
    })
    .sort((a, b) => {
      const byFamily =
        familyOrder.indexOf(a.family) - familyOrder.indexOf(b.family);
      if (byFamily !== 0) return byFamily;
      return numeric(a.sortToken, b.sortToken);
    });

  return (
    <div className="coverage-panel">
      <div className="coverage-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          Coverage grid
        </div>
        <h2 className="t-heading-md" style={{ marginTop: 4 }}>
          Every model, every architecture, at a glance.
        </h2>
        <p
          className="t-body-sm"
          style={{ color: "var(--text-secondary)", marginTop: 6, maxWidth: 640 }}
        >
          Columns are every chip with production support on the current
          release. A green dot means the model runs there in production.
        </p>
      </div>
      <div className="coverage-scroll">
        <table className="coverage-table">
          <thead>
            <tr>
              <th className="coverage-model-col">Model</th>
              {chips.map((chip) => (
                <th key={chip}>
                  {HARDWARE_CHIPS.includes(chip) ? (
                    <Link
                      href={`/photon/hardware/${slugify(chip)}`}
                      className="coverage-chip-label"
                    >
                      {chip}
                    </Link>
                  ) : (
                    <span className="coverage-chip-label">{chip}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">
                  <Link
                    href={`/photon/models/${row.familySlug}`}
                    className="coverage-row-link"
                  >
                    {row.label}
                  </Link>
                  {row.showReleases ? (
                    <span className="coverage-row-releases mono">
                      {row.releases.join(" · ")}
                    </span>
                  ) : null}
                </th>
                {chips.map((chip) => {
                  const supported = row.chips.has(chip);
                  return (
                    <td key={chip}>
                      <span
                        className={`coverage-dot ${supported ? "is-on" : "is-off"}`}
                        role="img"
                        aria-label={`${row.label} on ${chip}: ${
                          supported ? "production support" : "not supported"
                        }`}
                        data-tip={`${row.label} on ${chip} · ${
                          supported ? "Production" : "Not supported"
                        }`}
                        tabIndex={0}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="coverage-legend mono">
        <span>
          <span className="coverage-dot is-on" aria-hidden="true" /> Production
          on the current release
        </span>
        <span>
          <span className="coverage-dot is-off" aria-hidden="true" /> Not
          supported
        </span>
      </div>
    </div>
  );
};

export function SupportExplorer() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [view, setView] = useState<ViewMode>("model");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "model",
    dir: "asc",
  });

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const filtered = useMemo(
    () =>
      SUPPORT_MATRIX.filter(
        (row) =>
          (filters.model === "any" || row.model === filters.model) &&
          (filters.hardware === "any" || row.chip === filters.hardware) &&
          (filters.capability === "any" ||
            row.capabilities.includes(filters.capability)) &&
          (filters.precision === "any" ||
            row.precision.includes(filters.precision)),
      ),
    [filters],
  );

  const sortedRows = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const c = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? c : -c;
    });
    return rows;
  }, [filtered, sort]);

  const hardwareGroups = useMemo(() => {
    const groups = new Map<string, SupportRow[]>();
    for (const row of filtered) {
      const rows = groups.get(row.chip) ?? [];
      rows.push(row);
      groups.set(row.chip, rows);
    }
    return [...groups.entries()]
      .map(([chip, rows]) => ({
        chip,
        rows: rows.sort((a, b) => a.model.localeCompare(b.model)),
      }))
      .sort((a, b) => a.chip.localeCompare(b.chip));
  }, [filtered]);

  const requestMailto = useMemo(() => {
    const lines = [
      `Model: ${filters.model === "any" ? "" : filters.model}`,
      `Hardware: ${filters.hardware === "any" ? "" : filters.hardware}`,
      `Capability: ${filters.capability === "any" ? "" : filters.capability}`,
      `Precision: ${filters.precision === "any" ? "" : filters.precision}`,
      "",
      "What are you building?",
    ];
    return `${CONTACT_URL}?subject=${encodeURIComponent(
      "Photon support request",
    )}&body=${encodeURIComponent(lines.join("\n"))}`;
  }, [filters]);

  const onSort = (key: SortKey) =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "verified" ? "desc" : "asc" },
    );

  const downloadCsv = () => {
    const header = [
      "Model",
      "Family",
      "Chip",
      "Target",
      "Tested hardware",
      "Precision",
      "Capabilities",
      "Benchmarked",
      "Since",
      "Last verified",
    ];
    const rows = SUPPORT_MATRIX.map((r) => [
      r.model,
      r.family,
      r.chip,
      r.target,
      r.testedHardware ?? "",
      r.precision.join(" "),
      r.capabilities.join(" "),
      r.benchmarked ? "yes" : "no",
      r.since ?? "",
      r.lastVerified ?? "",
    ]);
    const escape = (value: string) => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...rows]
      .map((cols) => cols.map(escape).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "photon-support-matrix.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortTh = ({ id, children }: { id: SortKey; children: ReactNode }) => (
    <th
      aria-sort={
        sort.key === id
          ? sort.dir === "asc"
            ? "ascending"
            : "descending"
          : undefined
      }
    >
      <button type="button" className="matrix-sort" onClick={() => onSort(id)}>
        {children}
        <span className="matrix-sort-arrow" aria-hidden="true">
          {sort.key === id ? (sort.dir === "asc" ? "↑" : "↓") : ""}
        </span>
      </button>
    </th>
  );

  return (
    <div className="md-landing">
      <Nav
        theme={theme}
        onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
      />

      <main>
        <section className="section" style={{ paddingTop: 72, paddingBottom: 56 }}>
          <div className="container">
            <PhotonPageMark section="Support" />
            <h1 className="t-display-md" style={{ marginTop: 12, maxWidth: 780 }}>
              Production support.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              Every configuration listed here is production ready and tested
              on the named hardware.
            </p>
          </div>
        </section>

        <section
          className="section"
          style={{ paddingTop: 0, paddingBottom: 56 }}
        >
          <div className="container">
            <CoverageGrid />
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }} id="matrix">
          <div className="container">
            <div className="filter-bar">
              <FilterSelect
                label="Model"
                value={filters.model}
                options={["any", ...MODEL_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, model: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <FilterSelect
                label="Hardware"
                value={filters.hardware}
                options={["any", ...HARDWARE_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, hardware: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <FilterSelect
                label="Capability"
                value={filters.capability}
                options={["any", ...CAPABILITY_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, capability: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <FilterSelect
                label="Precision"
                value={filters.precision}
                options={["any", ...PRECISION_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, precision: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <div className="filter-views seg" role="group" aria-label="View">
                <button
                  type="button"
                  className={view === "model" ? "is-active" : ""}
                  onClick={() => setView("model")}
                >
                  By model
                </button>
                <button
                  type="button"
                  className={view === "hardware" ? "is-active" : ""}
                  onClick={() => setView("hardware")}
                >
                  By hardware
                </button>
              </div>
            </div>

            <p className="matrix-summary mono">
              {filtered.length} of {SUPPORT_MATRIX.length} configurations
            </p>

            {filtered.length > 0 ? (
              <div className="install-strip">
                <div>
                  <div className="t-heading-sm">
                    {filtered.length}{" "}
                    {filtered.length === 1 ? "configuration" : "configurations"}{" "}
                    match
                  </div>
                  <p
                    className="t-body-sm"
                    style={{ color: "var(--text-secondary)", marginTop: 4 }}
                  >
                    Install Photon and run locally.
                  </p>
                </div>
                <InstallSnippet />
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <div className="honest-card" style={{ marginTop: 0 }}>
                <div
                  className="t-eyebrow"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  No matching configuration
                </div>
                <h3 className="t-heading-md" style={{ marginTop: 10 }}>
                  This combination is not supported yet.
                </h3>
                <p
                  className="t-body-md"
                  style={{
                    color: "var(--text-secondary)",
                    marginTop: 10,
                    maxWidth: 640,
                  }}
                >
                  Tell us which model and hardware you need.
                </p>
                <div className="final-ctas" style={{ marginTop: 20 }}>
                  <a href={requestMailto} className="btn btn-primary">
                    Request this configuration <IconArrowUpRight size={14} />
                  </a>
                  <a href={CONTACT_URL} className="btn btn-secondary">
                    Contact us
                  </a>
                </div>
              </div>
            ) : view === "model" ? (
              <div className="matrix-panel">
                <table className="matrix-table">
                  <thead>
                    <tr>
                      <SortTh id="model">Model</SortTh>
                      <SortTh id="target">Target</SortTh>
                      <SortTh id="tested">Tested hardware</SortTh>
                      <th>Precision</th>
                      <th>Capabilities</th>
                      <SortTh id="since">Since</SortTh>
                      <SortTh id="verified">Last verified</SortTh>
                      <th>Benchmark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr key={row.id} id={row.id}>
                        <td>
                          <ModelCell row={row} />
                        </td>
                        <td className="matrix-dim">{row.target}</td>
                        <td className="matrix-dim">
                          {row.testedHardware ? (
                            <Link
                              href={`/photon/hardware/${slugify(row.chip)}`}
                              className="matrix-hw-link"
                            >
                              {row.testedHardware}
                            </Link>
                          ) : (
                            "–"
                          )}
                        </td>
                        <td className="mono tabular matrix-dim">
                          {row.precision.join(" · ")}
                        </td>
                        <td className="matrix-caps">
                          {row.capabilities.join(" · ")}
                        </td>
                        <td className="mono tabular matrix-dim">
                          {row.since ? `v${row.since}` : "–"}
                        </td>
                        <td className="mono tabular matrix-dim">
                          {row.lastVerified
                            ? formatMatrixDate(row.lastVerified)
                            : "–"}
                        </td>
                        <td>
                          <BenchmarkCell row={row} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="matrix-groups">
                {hardwareGroups.map((group) => (
                  <div className="matrix-group" key={group.chip}>
                    <div className="matrix-group-head">
                      <Link
                        href={`/photon/hardware/${slugify(group.chip)}`}
                        className="t-heading-md matrix-hw-link"
                      >
                        {group.chip}
                      </Link>
                      <div className="matrix-group-sub mono">
                        {group.rows[0].target} ·{" "}
                        tested on {group.rows[0].testedHardware}{" "}
                        · {group.rows.length}{" "}
                        {group.rows.length === 1
                          ? "configuration"
                          : "configurations"}
                      </div>
                    </div>
                    <div className="matrix-panel">
                      <table className="matrix-table">
                        <thead>
                          <tr>
                            <th>Model</th>
                            <th>Precision</th>
                            <th>Capabilities</th>
                            <th>Since</th>
                            <th>Last verified</th>
                            <th>Benchmark</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => (
                            <tr key={row.id} id={row.id}>
                              <td>
                                <ModelCell row={row} />
                              </td>
                              <td className="mono tabular matrix-dim">
                                {row.precision.join(" · ")}
                              </td>
                              <td className="matrix-caps">
                                {row.capabilities.join(" · ")}
                              </td>
                              <td className="mono tabular matrix-dim">
                                {row.since ? `v${row.since}` : "–"}
                              </td>
                              <td className="mono tabular matrix-dim">
                                {row.lastVerified
                                  ? formatMatrixDate(row.lastVerified)
                                  : "–"}
                              </td>
                              <td>
                                <BenchmarkCell row={row} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="matrix-foot mono">
              Updated by release · <Link href="/photon/updates">updates</Link> ·{" "}
              <button type="button" className="matrix-csv" onClick={downloadCsv}>
                Download matrix (CSV)
              </button>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
